import React, { useMemo } from "react";

import { DatabaseIndexDto } from "../lib/generated/api-client";

type Props = {
  indexes?: DatabaseIndexDto[];
  /** Primary key name, so the PK's own index can be labelled as such. */
  primaryFieldName?: string;
};

/**
 * Postgres and MySQL both report the primary key as an ordinary unique index
 * (`<table>_pkey`, `PRIMARY`). It is already implied by the key column in the
 * grid, so it is labelled rather than dropped: hiding it would misreport the
 * index count against what the database actually has.
 */
const isPrimaryIndex = (
  index: DatabaseIndexDto,
  primaryFieldName?: string
): boolean => {
  const name = index.name?.toLowerCase() ?? "";
  if (name === "primary" || name.endsWith("_pkey")) return true;
  return (
    !!primaryFieldName &&
    index.unique &&
    index.fields?.length === 1 &&
    index.fields[0] === primaryFieldName
  );
};

const sortIndexes = (
  indexes: DatabaseIndexDto[],
  primaryFieldName?: string
): DatabaseIndexDto[] =>
  [...indexes].sort((a, b) => {
    // Primary first, then unique, then the rest — alphabetical inside a group.
    const rank = (index: DatabaseIndexDto) =>
      isPrimaryIndex(index, primaryFieldName) ? 0 : index.unique ? 1 : 2;
    const delta = rank(a) - rank(b);
    if (delta !== 0) return delta;
    return (a.name ?? "").localeCompare(b.name ?? "");
  });

const Badge = ({
  label,
  tone,
  title,
}: {
  label: string;
  tone: "primary" | "info";
  title: string;
}) => {
  const color = tone === "primary" ? "var(--od-primary)" : "var(--od-info)";
  return (
    <span
      className="inline-flex shrink-0 items-center rounded-full border px-1.5 py-px text-xs font-medium"
      style={{
        color: tone === "primary" ? "var(--od-primary-text)" : color,
        borderColor: `color-mix(in srgb, ${color} 40%, transparent)`,
        backgroundColor: `color-mix(in srgb, ${color} 10%, transparent)`,
      }}
      title={title}
    >
      {label}
    </span>
  );
};

/**
 * Lists the indexes the analyser found on a collection: name, the columns it
 * covers in index order, and whether it is unique. Column order is significant
 * for a composite index (a `(title, year)` btree does not serve `year` alone),
 * so the fields are rendered in the order the connector returned them and are
 * never sorted.
 */
export const IndexList = ({ indexes, primaryFieldName }: Props) => {
  const ordered = useMemo(
    () => sortIndexes(indexes ?? [], primaryFieldName),
    [indexes, primaryFieldName]
  );

  if (!ordered.length) return null;

  return (
    <section className="flex shrink-0 flex-col gap-1.5">
      <h3 className="m-0 text-xs font-medium uppercase tracking-wide text-od-muted">
        Indexes
        <span className="ml-1.5 font-normal normal-case tracking-normal">
          {ordered.length}
        </span>
      </h3>
      <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
        {ordered.map((index) => {
          const primary = isPrimaryIndex(index, primaryFieldName);
          const fields = index.fields ?? [];
          return (
            <li
              key={index.id ?? index.name}
              className="flex min-w-0 items-center gap-1.5 rounded-lg border border-od-border bg-od-surface-2 px-2 py-1"
              title={
                fields.length
                  ? `${index.name} on ${fields.join(", ")}`
                  : index.name
              }
            >
              <span className="truncate text-xs font-medium text-od-text">
                {index.name}
              </span>
              {fields.length > 0 && (
                <span className="truncate font-mono text-xs text-od-muted">
                  {fields.join(", ")}
                </span>
              )}
              {primary && <Badge label="PK" tone="primary" title="Primary key" />}
              {!primary && index.unique && (
                <Badge
                  label="Unique"
                  tone="info"
                  title="Values in these columns must be unique"
                />
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default IndexList;
